/**
 * Random pipe compatibility report API client.
 * Uses REACT_APP_PIPEFITTER_RANDOM_PIPE_COMPATIBILITY directly (no localhost proxy).
 *
 * Auth: send `X-Api-Key` from REACT_APP_PIPEFITTER_RANDOM_PIPE_COMPAT_API_KEY.
 * History "Requested by": submit also sends `X-Requested-By` with the logged-in user name.
 */
import axios from 'axios';
import type {
  CompatPollResponse,
  CompatReportDocument,
  CompatReportListResponse,
  CompatSequenceResponse,
  CompatStoredReportDetail,
  CompatSubmitRequest,
  CompatTicketResponse,
} from '../components/randompipecompat/types';
import { resolveRequestedByName } from '../components/randompipecompat/helpers/resolveRequestedByName';

const baseURL = (
  process.env.REACT_APP_PIPEFITTER_RANDOM_PIPE_COMPATIBILITY ||
  'https://bjqnfy70g1.execute-api.us-west-2.amazonaws.com/v1/'
).replace(/\/?$/, '/');

const randomPipeCompatApi = axios.create({
  baseURL,
  headers: {
    Accept: 'application/json',
    'Content-Type': 'application/json',
  },
});

const apiKey = (
  process.env.REACT_APP_PIPEFITTER_RANDOM_PIPE_COMPAT_API_KEY ||
  ''
).trim();
if (apiKey) {
  randomPipeCompatApi.defaults.headers.common['X-Api-Key'] = apiKey;
}

function isReportDocument(data: unknown): data is CompatReportDocument {
  if (!data || typeof data !== 'object') return false;
  const d = data as Record<string, unknown>;
  return Boolean(d.weldability && d.project);
}

/**
 * Normalize POST response: ticket (async) or full report document (sync/local path).
 */
export function normalizeSubmitResponse(data: unknown): CompatTicketResponse {
  if (!data || typeof data !== 'object') {
    throw new Error('Empty compatibility report response');
  }
  const d = data as Record<string, unknown>;

  if (d.status === 'working' || d.status === 'done' || d.status === 'failed') {
    return data as CompatTicketResponse;
  }

  if (isReportDocument(data)) {
    return {
      jobId: typeof d.jobId === 'string' ? d.jobId : 'sync',
      status: 'done',
      result: data,
    };
  }

  throw new Error('Unexpected compatibility report response shape');
}

export async function submitCompatibilityReport(
  body: CompatSubmitRequest
): Promise<CompatTicketResponse> {
  // Backend stores "Requested by" from X-Requested-By (not a JSON body field).
  const requestedBy = resolveRequestedByName();
  const { data } = await randomPipeCompatApi.post('compatibility-reports', body, {
    headers: requestedBy ? { 'X-Requested-By': requestedBy } : undefined,
  });
  return normalizeSubmitResponse(data);
}

export async function getCompatibilityReport(jobId: string): Promise<CompatPollResponse> {
  const { data } = await randomPipeCompatApi.get(
    `compatibility-reports/${encodeURIComponent(jobId)}`
  );
  return normalizeSubmitResponse(data);
}

/**
 * Exact arrival order for one Monte Carlo run.
 * PRD: never shuffle/recompute in the frontend — Python's RNG cannot be reproduced in JS.
 */
export async function getCompatibilityReportSequence(
  reportId: string,
  runIndex: number
): Promise<CompatSequenceResponse> {
  const { data } = await randomPipeCompatApi.get(
    `compatibility-reports/${encodeURIComponent(reportId)}/sequence/${encodeURIComponent(String(runIndex))}`
  );
  if (!data || typeof data !== 'object' || !Array.isArray((data as CompatSequenceResponse).order)) {
    throw new Error('Unexpected sequence response shape');
  }
  return data as CompatSequenceResponse;
}

/**
 * Project report index (newest first).
 * GET /v1/compatibility-reports?projectNumber=…
 */
export async function listCompatibilityReports(
  projectNumber: string
): Promise<CompatReportListResponse> {
  const { data } = await randomPipeCompatApi.get('compatibility-reports', {
    params: { projectNumber },
  });
  if (!data || typeof data !== 'object' || !Array.isArray((data as CompatReportListResponse).reports)) {
    throw new Error('Unexpected compatibility report list response');
  }
  return data as CompatReportListResponse;
}

/**
 * One finished report by id — history store shape or poll-done ticket.
 * Prefer this for History UI (not live polling).
 */
export async function getStoredCompatibilityReport(
  reportId: string
): Promise<CompatStoredReportDetail> {
  const { data } = await randomPipeCompatApi.get(
    `compatibility-reports/${encodeURIComponent(reportId)}`
  );
  return normalizeStoredReportDetail(data, reportId);
}

/**
 * Stakeholder PDF from the random-compat API.
 * GET /v1/compatibility-reports/{id}/pdf → { reportId, key, url, expiresInSeconds }
 * Then auto-download from the presigned `url`.
 */
export async function downloadCompatibilityReportPdf(reportId: string): Promise<void> {
  try {
    const { data } = await randomPipeCompatApi.get(
      `compatibility-reports/${encodeURIComponent(reportId)}/pdf`
    );
    const payload = data as {
      url?: string;
      key?: string;
      reportId?: string;
      error?: { message?: string; code?: string };
      reason?: string;
      message?: string;
    };

    const signedUrl = typeof payload?.url === 'string' ? payload.url.trim() : '';
    if (!signedUrl) {
      throw new Error(
        payload?.error?.message ||
          payload?.reason ||
          payload?.message ||
          'PDF URL missing from response'
      );
    }

    const fromKey =
      typeof payload.key === 'string' && payload.key.includes('/')
        ? payload.key.split('/').pop()
        : payload.key;
    const filename = fromKey || `${reportId}-compatibility-report.pdf`;

    await triggerUrlDownload(signedUrl, filename);
  } catch (err) {
    const ax = err as { response?: { data?: Blob | Record<string, unknown> | string } };
    if (ax?.response?.data instanceof Blob) {
      throw new Error(await messageFromErrorBlob(ax.response.data));
    }
    if (ax?.response?.data && typeof ax.response.data === 'object') {
      const d = ax.response.data as {
        error?: { message?: string };
        reason?: string;
        message?: string;
      };
      throw new Error(d.error?.message || d.reason || d.message || 'PDF generation failed');
    }
    throw err;
  }
}

/** Prefer blob download (sets filename); fall back to opening the signed URL. */
async function triggerUrlDownload(signedUrl: string, filename: string): Promise<void> {
  try {
    const res = await fetch(signedUrl);
    if (!res.ok) {
      throw new Error(`Download failed (${res.status})`);
    }
    const blob = await res.blob();
    const objectUrl = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = objectUrl;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    a.remove();
    URL.revokeObjectURL(objectUrl);
  } catch {
    const a = document.createElement('a');
    a.href = signedUrl;
    a.download = filename;
    a.target = '_blank';
    a.rel = 'noopener noreferrer';
    document.body.appendChild(a);
    a.click();
    a.remove();
  }
}

async function messageFromErrorBlob(blob: Blob): Promise<string> {
  const text = await blob.text();
  try {
    const parsed = JSON.parse(text) as {
      error?: { message?: string; code?: string };
      reason?: string;
      message?: string;
    };
    return (
      parsed.error?.message ||
      parsed.reason ||
      parsed.message ||
      text ||
      'PDF generation failed'
    );
  } catch {
    return text || 'PDF generation failed';
  }
}

export function normalizeStoredReportDetail(
  data: unknown,
  fallbackId: string
): CompatStoredReportDetail {
  if (!data || typeof data !== 'object') {
    throw new Error('Empty stored compatibility report response');
  }
  const d = data as Record<string, unknown>;

  // History store: { reportId, result, request, settings, … }
  // Also matches poll-done when it carries reportId — normalize missing request/settings.
  if (isReportDocument(d.result) && (typeof d.reportId === 'string' || typeof d.jobId === 'string')) {
    const result = d.result as CompatReportDocument;
    const id =
      (typeof d.reportId === 'string' && d.reportId) ||
      (typeof d.jobId === 'string' && d.jobId) ||
      fallbackId;
    const request =
      d.request && typeof d.request === 'object'
        ? (d.request as Record<string, unknown>)
        : {};
    const settings =
      d.settings && typeof d.settings === 'object'
        ? (d.settings as Record<string, unknown>)
        : (result.settings as unknown as Record<string, unknown>);
    return {
      reportId: id,
      projectNumber:
        (typeof d.projectNumber === 'string' && d.projectNumber) || result.project.number,
      createdAt:
        (typeof d.createdAt === 'string' && d.createdAt) || result.project.reportDate,
      requestedBy: typeof d.requestedBy === 'string' ? d.requestedBy : null,
      baseSeed:
        typeof d.baseSeed === 'number' ? d.baseSeed : (result.run.baseSeed ?? 0),
      request,
      settings,
      result,
    };
  }

  // Poll-done ticket: { jobId, status: 'done', result } — no request body on the ticket.
  if (d.status === 'done' && isReportDocument(d.result)) {
    const id =
      (typeof d.jobId === 'string' && d.jobId) ||
      (typeof d.reportId === 'string' && d.reportId) ||
      fallbackId;
    const result = d.result as CompatReportDocument;
    return {
      reportId: id,
      projectNumber: result.project.number,
      createdAt: typeof d.createdAt === 'string' ? d.createdAt : result.project.reportDate,
      requestedBy: typeof d.requestedBy === 'string' ? d.requestedBy : null,
      baseSeed: typeof d.baseSeed === 'number' ? d.baseSeed : (result.run.baseSeed ?? 0),
      request: (d.request as Record<string, unknown>) || {},
      settings:
        (d.settings as Record<string, unknown>) ||
        (result.settings as unknown as Record<string, unknown>),
      result,
    };
  }

  // Sync document as body (rare)
  if (isReportDocument(data)) {
    const result = data as CompatReportDocument;
    return {
      reportId: fallbackId,
      projectNumber: result.project.number,
      createdAt: result.project.reportDate,
      requestedBy: null,
      baseSeed: result.run.baseSeed ?? 0,
      request: {},
      settings: result.settings as unknown as Record<string, unknown>,
      result,
    };
  }

  throw new Error('Unexpected stored compatibility report response shape');
}

export function extractSubmitErrorMessage(err: unknown): string {
  const ax = err as {
    response?: {
      status?: number;
      data?: { message?: string; reason?: string; error?: string; detail?: string };
    };
    message?: string;
    code?: string;
  };
  const status = ax?.response?.status;
  if (status === 401 || status === 403) {
    return 'Unauthorized (missing or invalid X-Api-Key). Set REACT_APP_PIPEFITTER_RANDOM_PIPE_COMPAT_API_KEY in .env and restart the app.';
  }
  const data = ax?.response?.data;
  if (ax?.code === 'ERR_NETWORK' || ax?.message === 'Network Error') {
    return 'Network/CORS error calling compatibility report API. Confirm the API Gateway allows your origin and X-Api-Key.';
  }
  return (
    data?.reason ||
    data?.message ||
    data?.error ||
    data?.detail ||
    (status ? `Request failed (${status})` : null) ||
    ax?.message ||
    'Failed to submit compatibility report'
  );
}

export default randomPipeCompatApi;
